import { NotificationApiService } from "@Api/notification";
import { useFetchingListingApi } from "@Hooks/useFetchListingApi";
import { STALE_TIME } from "@Constants/api";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { NotificationCode } from "@Constants/enums";
import { NavigationRoutes } from "@Navigation/NavigationRoutes";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import NotificationService from "../../../services/NotificationService";

export interface NotificationItem {
  id: string;
  title: string;
  description: string;
  timestamp: string;
  type: "dispute" | "evidence" | "review" | "payment";
  read?: boolean;
}

interface NotificationContainerProps {
  isOpen?: boolean;
  onClose?: () => void;
}

const GENERAL_ERROR = "Something went wrong. Please try again later.";
const NOTIFICATION_PAGE_SIZE = 20;

const NotificationContainer = ({ isOpen = false, onClose }: NotificationContainerProps = {}) => {
  const [readNotifications, setReadNotifications] = useState<Set<string>>(new Set());
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const notificationListBaseParams = {
    pageSize: NOTIFICATION_PAGE_SIZE,
    sortBy: "timestamp",
    sortOrder: "desc",
  };

  const {
    data: notificationList,
    isLoading: isNotificationListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["notification-list", notificationListBaseParams],
    queryFn: async ({ pageParam = 1 }) => {
      const { ok, response, data } = await NotificationApiService.getNotificationListings({
        ...notificationListBaseParams,
        page: pageParam,
      });

      if (ok) {
        return data;
      }

      NotificationService.error(response?.message || GENERAL_ERROR);
      throw new Error(GENERAL_ERROR);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.pagination;
      const currentPage = pagination?.currentPage ?? 1;
      const totalPages = pagination?.totalPages ?? 1;
      const hasNext =
        pagination?.hasNextPage ?? (Number.isFinite(totalPages) && currentPage < totalPages);

      return hasNext ? currentPage + 1 : undefined;
    },
    staleTime: STALE_TIME,
    enabled: isOpen,
  });

  const { data: notificationCount, isFetching: isNotificationCountLoading } =
    useFetchingListingApi({
      queryParams: {},
      apiService: NotificationApiService.getNotificationCount,
      queryKeyValue: "notification-count",
    });

  // Mutation for marking notifications as read
  const markAsReadMutation = useMutation({
    mutationFn: (payload: any) => NotificationApiService.notificationMarkAsRead(payload),
    onSuccess: () => {
      // Refetch notification list and count after marking as read
      queryClient.invalidateQueries({ queryKey: ["notification-list"] });
      queryClient.invalidateQueries({ queryKey: ["notification-count"] });
    },
  });

  const getNavigationPath = (notification: any) => {
    const { code, data } = notification;
    const { DASHBOARD_ROUTES } = NavigationRoutes;

    switch (code) {
      // Dispute related notifications 
      case NotificationCode.DISPUTE_RAISED:
      case NotificationCode.MEDIATION_STARTED:
      case NotificationCode.DISPUTE_READY_RESOLUTION:
      case NotificationCode.DISPUTE_RESOLVED:
      case NotificationCode.FINANCIAL_ACTON_TAKEN:
        if (data?.DisputeId) {
          return `${DASHBOARD_ROUTES.DISPUTE_DETAILS}/${data.DisputeId}`;
        }
        return DASHBOARD_ROUTES.DISPUTES_MANAGEMENT;

      // Booking related notifications 
      case NotificationCode.BOOKING_CREATED:
      case NotificationCode.SECURITY_DEPOSIT_HOLD:
      case NotificationCode.DEPOSIT_RELEASED:
      case NotificationCode.BOOKING_CLOSED:
        if (data?.BookingId) {
          return `${DASHBOARD_ROUTES.BOOKING_DETAILS}/${data.BookingId}`;
        }
        return DASHBOARD_ROUTES.BOOKINGS_MANAGEMENT;

      // Withdrawal request 
      case NotificationCode.WITHDRAWAL_REQUEST_RECIEVED:
        if (data?.TransactionId) {
          return `${DASHBOARD_ROUTES.WITHDRAWAL_DETAILS}/${data.TransactionId}/${data.UserId}`;
        }
        return DASHBOARD_ROUTES.WITHDRAWAL_MANAGEMENT;

      // Review related 
      case NotificationCode.PRODUCT_REVIEWED:
      case NotificationCode.USER_REVIEWED:
        return DASHBOARD_ROUTES.REVIEWS_MANAGEMENT;

      default:
        return DASHBOARD_ROUTES.DASHBOARD;
    }
  };

  const handleMarkAllRead = () => {
    const payload = {
      notificationIds: [0],
      allSelected: true,
      markAllRead: true,
      status: "Read"
    };
    markAsReadMutation.mutate(payload);
    
    const allNotifications = notificationList?.pages?.flatMap((page: any) => page?.data || []) || [];
    if (allNotifications.length > 0) {
      const allIds = allNotifications.map((n: any) => n.id);
      setReadNotifications(new Set(allIds));
    }
  };

  const handleNotificationClick = (notification: any) => {
    const payload = {
      notificationIds: [notification.id],
      allSelected: false,
      markAllRead: false,
      status: "Read"
    };
    markAsReadMutation.mutate(payload);
    
    // Mark notification as read 
    setReadNotifications(prev => new Set(prev).add(notification.id));

    const path = getNavigationPath(notification);
    navigate(path);
    onClose?.();
  };

  return {
    notificationList,
    notificationItems: notificationList?.pages?.flatMap((page: any) => page?.data || []) || [],
    isNotificationListLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    notificationCount,
    isNotificationCountLoading,
    handleMarkAllRead,
    handleNotificationClick,
    isMarkingAsRead: markAsReadMutation.isPending,
    readNotifications,
  };
};

export default NotificationContainer;
